import { setUser } from "@sentry/react";
import type { User } from "@supabase/supabase-js";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { identifyUser, resetAnalytics, trackEvent } from "@/lib/analytics";
import { AuthStatus } from "@/lib/constant";
import { isNativeApp, nativeBridge } from "@/lib/native";
import { supabase } from "@/lib/supabaseClient";

export interface AuthState {
  user: User | null;
  status: AuthStatus;
  setUser: (user: User | null) => void;
  initialize: () => void;
  logout: () => Promise<void>;
  updateDisplayName: (name: string) => Promise<unknown>;
  uploadAvatar: (file: File) => Promise<unknown>;
  deleteAccount: () => Promise<unknown>;
}

const avatarStoragePath = (userId: string) => `${userId}/avatar`;

export const useAuthStore = create<AuthState>()(
  devtools((set, get) => ({
    user: null,
    status: AuthStatus.LOADING,
    initialize: () => {
      // 로컬 스토리지 캐시에서 초기 세션을 빠르게 읽어 상태를 설정
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (session) {
          set({ user: session.user, status: AuthStatus.AUTHENTICATED });
          return;
        }

        // Android 네이티브 앱: OAuth 콜백이 SecureStore에 저장한 토큰으로 세션 복원
        if (isNativeApp && /android/i.test(navigator.userAgent)) {
          const nativeSession = await nativeBridge.auth.getSession();
          if (nativeSession) {
            await supabase.auth.setSession({
              access_token: nativeSession.accessToken,
              refresh_token: nativeSession.refreshToken,
            });
            return;
          }
        }

        set({ user: null, status: AuthStatus.UNAUTHENTICATED });
      });

      // 이후 모든 인증 상태 변화를 단일 구독으로 처리
      supabase.auth.onAuthStateChange((event, session) => {
        if (session?.user) {
          if (event === "SIGNED_IN") {
            trackEvent("signin_success", {
              provider: session.user.app_metadata.provider ?? "unknown",
            });
          }
          setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata.display_name,
          });

          identifyUser(session.user.id, {
            email: session.user.email ?? "",
            name: session.user.user_metadata.display_name ?? "",
          });

          // 네이티브 SecureStore에 세션 토큰 동기화
          nativeBridge.auth.saveSession(session.access_token, session.refresh_token);

          // RevenueCat 사용자 연결 (구독이 올바른 계정에 귀속되도록)
          nativeBridge.purchase.logIn(session.user.id);
        } else {
          setUser(null);
          resetAnalytics();
          nativeBridge.auth.clearSession();
          nativeBridge.purchase.logOut();
        }

        set({
          user: session?.user ?? null,
          status: session ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED,
        });
      });
    },
    logout: async () => {
      trackEvent("logout");
      setUser(null);
      resetAnalytics();
      set({ user: null, status: AuthStatus.UNAUTHENTICATED });
      nativeBridge.auth.clearSession();
      nativeBridge.purchase.logOut();
      await supabase.auth.signOut();
    },
    updateDisplayName: async (name: string) => {
      const { data, error } = await supabase.auth.updateUser({ data: { display_name: name } });

      if (error) {
        throw error;
      }

      trackEvent("profile_updated", { field: "display_name" });
      setUser({
        id: data.user.id,
        email: data.user.email,
        name: data.user.user_metadata.display_name,
      });
      set({ user: data.user });
    },
    uploadAvatar: async (file: File) => {
      const userId = get().user?.id ?? "";
      const path = avatarStoragePath(userId);

      if (userId) {
        const { error: uploadError } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });

        if (uploadError) {
          throw uploadError;
        }

        const {
          data: { publicUrl },
        } = supabase.storage.from("avatars").getPublicUrl(path);
        const { data, error: updateError } = await supabase.auth.updateUser({ data: { avatar_url: publicUrl } });

        if (updateError) {
          throw updateError;
        }

        trackEvent("profile_updated", { field: "avatar" });
        set({ user: data.user });
      }
    },
    deleteAccount: async () => {
      const userId = get().user?.id ?? "";

      trackEvent("account_deleted");

      if (userId) {
        await supabase.storage.from("avatars").remove([avatarStoragePath(userId)]);
        await supabase.rpc("delete_user");
      }

      setUser(null);
      resetAnalytics();
      nativeBridge.auth.clearSession();
      nativeBridge.purchase.logOut();
      await supabase.auth.signOut({ scope: "local" });
      set({ user: null, status: AuthStatus.UNAUTHENTICATED });
    },
  })),
);

// Selectors
export const selectDisplayName = (state: AuthState) =>
  state.user?.user_metadata?.display_name ??
  state.user?.user_metadata?.full_name ??
  state.user?.user_metadata?.name ??
  "학습자";
export const selectAvatarUrl = (state: AuthState): string | undefined =>
  state.user?.user_metadata?.avatar_url ?? undefined;
